import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';

interface MediaFile {
    id: number;
    name: string;
    title?: string;
    description?: string;
    path: string;
    type: string;
    folder: string;
}

interface MediaEditModalProps {
    file: MediaFile | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export default function MediaEditModal({ file, isOpen, onClose, onSave }: MediaEditModalProps) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Update state when file changes
    useEffect(() => {
        if (file) {
            setTitle(file.title || '');
            setDescription(file.description || '');
        }
    }, [file]);

    const handleSave = async () => {
        if (!file) return;

        setIsSaving(true);
        try {
            await client.postApiMediaFileUpdateId({
                id: file.id.toString(),
                postApiMediaFileUpdateIdRequest: {
                    title: title || undefined,
                    description: description || undefined,
                }
            });

            Swal.fire({
                title: 'Success!',
                text: 'Media details updated successfully',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });

            onSave();
            onClose();
        } catch (err: any) {
            console.error('Failed to update file:', err);
            Swal.fire({
                title: 'Error',
                text: err.response?.data?.messages || err.response?.data?.message || err.message || 'Failed to update media details. Please try again.',
                icon: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen || !file) return null;

    return (
        <div className="fixed inset-0 bg-brand-950/40 backdrop-blur-sm flex items-center justify-center z-[99] p-4 lg:p-10 animate-in fade-in duration-300" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full flex flex-col overflow-hidden border border-gray-100 dark:border-gray-700 animate-in zoom-in-95 duration-300"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-black/20">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white">Edit Details</h3>
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-widest mt-0.5">Update media information</p>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-white dark:hover:bg-gray-700 rounded-full transition-all shadow-sm">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form */}
                <div className="p-6 space-y-6">
                    <div>
                        <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                            Current Filename
                        </label>
                        <div className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700 rounded-2xl text-gray-500 dark:text-gray-400 text-sm font-medium italic truncate shadow-inner">
                            {file.name}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="relative group">
                            <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2 ml-1 group-focus-within:text-brand-500 transition-colors">
                                Title <span className="text-[10px] font-medium lowercase">({title.length}/50)</span>
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                maxLength={50}
                                placeholder="Give this file a catchy title..."
                                className="w-full px-5 py-3.5 border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500 transition-all font-medium"
                            />
                        </div>

                        <div className="relative group">
                            <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2 ml-1 group-focus-within:text-brand-500 transition-colors">
                                Description <span className="text-[10px] font-medium lowercase">({description.length}/255)</span>
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                maxLength={255}
                                rows={4}
                                placeholder="Describe what this media is about..."
                                className="w-full px-5 py-3.5 border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500/10 focus:border-brand-500 transition-all font-medium resize-none custom-scrollbar"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-3 bg-gray-50/50 dark:bg-black/20">
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-6 py-3 text-sm font-bold text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 rounded-xl transition-all shadow-sm active:scale-95 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-8 py-3 bg-brand-500 text-white rounded-xl hover:bg-brand-600 shadow-lg shadow-brand-500/20 hover:shadow-brand-500/40 transition-all active:scale-95 disabled:opacity-50 font-bold text-sm"
                    >
                        {isSaving ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                Saving...
                            </>
                        ) : (
                            'Save Changes'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
